import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  GitCompare,
  Layers,
  Download
} from 'lucide-react';
import { ShotData } from '../types';
import { cn } from '@/lib/utils';

interface MultiShotComparisonProps {
  shots: ShotData[];
  className?: string;
  maxComparisons?: number;
  onExport?: (comparisonData: any) => void;
}

export const MultiShotComparison: React.FC<MultiShotComparisonProps> = ({
  shots,
  className,
  maxComparisons = 5,
  onExport
}) => {
  const [selectedShots, setSelectedShots] = useState<string[]>([]);
  const [overlayMode, setOverlayMode] = useState<'side-by-side' | 'overlay'>('side-by-side');
  const [visibleMetrics, _setVisibleMetrics] = useState({
    distance: true,
    power: true,
    speed: true,
    quality: true,
    timing: true,
    sweetSpot: true
  });

  // Toggle shot selection
  const toggleShotSelection = (shotId: string) => {
    setSelectedShots(prev => {
      if (prev.includes(shotId)) {
        return prev.filter(id => id !== shotId);
      }
      if (prev.length >= maxComparisons) {
        return prev;
      }
      return [...prev, shotId];
    });
  };

  // Get selected shot data
  const selectedShotData = useMemo(() => {
    return shots.filter(shot => selectedShots.includes(shot.id!));
  }, [shots, selectedShots]);

  // Prepare comparison data
  const comparisonData = useMemo(() => {
    if (selectedShotData.length === 0) {
      return {
        radarData: [],
        barData: [],
        trajectoryData: [],
        metricsTable: []
      };
    }

    // Radar chart data for multi-dimensional comparison
    const metrics = ['Distance', 'Power', 'Speed', 'Accuracy', 'Timing', 'Quality'];
    const radarData = metrics.map(metric => {
      const dataPoint: any = { metric };
      selectedShotData.forEach((shot, index) => {
        const shotLabel = `Shot ${index + 1}`;
        switch (metric) {
          case 'Distance':
            dataPoint[shotLabel] = shot.distance || 0;
            break;
          case 'Power':
            dataPoint[shotLabel] = (shot.powerTransfer || 0) * 100;
            break;
          case 'Speed':
            dataPoint[shotLabel] = shot.exitVelocity || 0;
            break;
          case 'Accuracy':
            dataPoint[shotLabel] = shot.sweetSpotDistance ? Math.max(0, 100 - shot.sweetSpotDistance * 5) : 0;
            break;
          case 'Timing':
            dataPoint[shotLabel] = shot.timing === 'excellent' ? 100 :
                                   shot.timing === 'good' ? 75 : 50;
            break;
          case 'Quality':
            dataPoint[shotLabel] = shot.quality === 'excellent' ? 100 :
                                   shot.quality === 'good' ? 75 :
                                   shot.quality === 'fair' ? 50 : 25;
            break;
        }
      });
      return dataPoint;
    });

    // Bar chart data for direct comparison
    const barData = selectedShotData.map((shot, index) => ({
      name: `Shot ${index + 1}`,
      distance: shot.distance || 0,
      power: (shot.powerTransfer || 0) * 100,
      speed: shot.exitVelocity || 0,
      sweetSpot: shot.sweetSpotDistance || 0,
      launchAngle: shot.launchAngle || 0
    }));

    // Trajectory data for overlay visualization
    const trajectoryData = selectedShotData.map((shot, index) => {
      const points = [];
      const maxDistance = shot.distance || 50;
      const launchAngle = (shot.launchAngle || 45) * Math.PI / 180;
      const exitVelocity = shot.exitVelocity || 30;
      
      for (let t = 0; t <= 4; t += 0.1) {
        const x = exitVelocity * Math.cos(launchAngle) * t;
        const y = exitVelocity * Math.sin(launchAngle) * t - 0.5 * 9.81 * t * t;
        if (y >= 0 && x <= maxDistance) {
          points.push({ x, y, shot: `Shot ${index + 1}` });
        }
      }
      return points;
    }).flat();

    // Metrics table for detailed comparison
    const metricsTable = selectedShotData.map((shot, index) => ({
      id: shot.id,
      label: `Shot ${index + 1}`,
      shotType: shot.shotType || 'Unknown',
      distance: `${shot.distance?.toFixed(1)}m`,
      exitVelocity: `${shot.exitVelocity?.toFixed(1)}m/s`,
      launchAngle: `${shot.launchAngle?.toFixed(1)}°`,
      horizontalAngle: `${shot.horizontalAngle?.toFixed(1)}°`,
      powerTransfer: `${((shot.powerTransfer || 0) * 100).toFixed(0)}%`,
      sweetSpotDistance: `${shot.sweetSpotDistance?.toFixed(1)}mm`,
      quality: shot.quality || 'Unknown',
      timing: shot.timing || 'Unknown',
      footwork: shot.footwork || 'Unknown'
    }));

    return {
      radarData,
      barData,
      trajectoryData,
      metricsTable
    };
  }, [selectedShotData]);

  const handleExport = () => {
    if (onExport) {
      onExport({
        selectedShots: selectedShotData,
        comparisonData,
        timestamp: new Date().toISOString()
      });
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <GitCompare className="h-6 w-6" />
            Multi-Shot Comparison
          </h2>
          <p className="text-muted-foreground">
            Select up to {maxComparisons} shots to compare
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedShots([])}
            disabled={selectedShots.length === 0}
          >
            Clear Selection
          </Button>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={selectedShots.length < 2}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Shot Selection Panel */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Shot Selection</CardTitle>
            <CardDescription>
              {selectedShots.length}/{maxComparisons} shots selected
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {shots.map((shot, index) => {
                  const isSelected = selectedShots.includes(shot.id!);
                  const isDisabled = !isSelected && selectedShots.length >= maxComparisons;
                  
                  return (
                    <div
                      key={shot.id || index}
                      className={cn(
                        'p-3 rounded-lg border transition-all',
                        isSelected ? 'bg-primary/10 border-primary' : '',
                        isDisabled ? 'opacity-50' : 'cursor-pointer hover:bg-accent'
                      )}
                      onClick={() => !isDisabled && toggleShotSelection(shot.id!)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onCheckedChange={() => toggleShotSelection(shot.id!)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">
                              Shot #{index + 1}
                            </span>
                            <Badge variant={isSelected ? 'default' : 'outline'}>
                              {shot.quality || 'Unknown'}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-1">
                            <div>Distance: {shot.distance?.toFixed(1)}m</div>
                            <div>Power: {((shot.powerTransfer || 0) * 100).toFixed(0)}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Comparison Visualization */}
        <Card className="col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Comparison Analysis</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={overlayMode === 'side-by-side' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOverlayMode('side-by-side')}
                >
                  Side by Side
                </Button>
                <Button
                  variant={overlayMode === 'overlay' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOverlayMode('overlay')}
                >
                  Overlay
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {selectedShots.length < 2 ? (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Layers className="h-12 w-12 mx-auto mb-3" />
                  <p>Select at least 2 shots to compare</p>
                </div>
              </div>
            ) : (
              <Tabs defaultValue="radar" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="radar">Radar</TabsTrigger>
                  <TabsTrigger value="metrics">Metrics</TabsTrigger>
                  <TabsTrigger value="trajectory">Trajectory</TabsTrigger>
                </TabsList>

                {/* Radar Comparison */}
                <TabsContent value="radar">
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={comparisonData.radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      {selectedShotData.map((_, index) => (
                        <Radar
                          key={index}
                          name={`Shot ${index + 1}`}
                          dataKey={`Shot ${index + 1}`}
                          stroke={COLORS[index]}
                          fill={COLORS[index]}
                          fillOpacity={0.3}
                        />
                      ))}
                      <Legend />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </TabsContent>

                {/* Metrics Comparison */}
                <TabsContent value="metrics">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={comparisonData.barData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {visibleMetrics.distance && <Bar dataKey="distance" fill="#3b82f6" name="Distance (m)" />}
                      {visibleMetrics.power && <Bar dataKey="power" fill="#10b981" name="Power (%)" />}
                      {visibleMetrics.speed && <Bar dataKey="speed" fill="#f59e0b" name="Speed (m/s)" />}
                    </BarChart>
                  </ResponsiveContainer>
                </TabsContent>

                {/* Trajectory Overlay */}
                <TabsContent value="trajectory">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={comparisonData.trajectoryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="x" name="Distance" unit="m" />
                      <YAxis dataKey="y" name="Height" unit="m" />
                      <Tooltip />
                      <Legend />
                      {selectedShotData.map((_, index) => (
                        <Line
                          key={index}
                          type="monotone"
                          dataKey="y"
                          data={comparisonData.trajectoryData.filter(p => p.shot === `Shot ${index + 1}`)}
                          stroke={COLORS[index]}
                          name={`Shot ${index + 1}`}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics Table */}
      {selectedShots.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Detailed Comparison</CardTitle>
            <CardDescription>Side-by-side metrics for selected shots</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Metric</th>
                    {comparisonData.metricsTable.map((shot, index) => (
                      <th key={index} className="text-center p-2">
                        <div className="flex items-center justify-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[index] }}
                          />
                          {shot.label}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(comparisonData.metricsTable[0] || {}).filter(key => key !== 'id' && key !== 'label').map(metric => (
                    <tr key={metric} className="border-b">
                      <td className="p-2 font-medium capitalize">{metric.replace(/([A-Z])/g, ' $1').trim()}</td>
                      {comparisonData.metricsTable.map((shot, index) => (
                        <td key={index} className="text-center p-2">
                          {shot[metric as keyof typeof shot]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};